import type { CollectAction, CollectOption, CollectUpdateByDelta } from "@/events/events";
import { CollectActionEvent, CollectChildEvent, CollectChildRemovedEvent, CollectOptionEvent, CollectUpdateByDeltaEvent } from "@/events/events";
import { Task } from "@lit/task";
import { ReactiveController, ReactiveControllerHost } from "lit";

type CollectHost = ReactiveControllerHost & {
  storeName            : symbol | string;
  noCollect            : boolean;
  _options             : Map<symbol | string, Promise<unknown[]>>;
  _actions             : Map<symbol | string, NonNullable<CollectAction["action"]>>;
  _partialOptions      : Map<symbol | string, NonNullable<CollectAction["action"]>>;
  _partialBundleOptions: Map<symbol | string, NonNullable<CollectAction["action"]>>;
  _deltaUpdates        : Map<symbol | string, NonNullable<CollectUpdateByDelta["update"]>>;
  dispatchEvent(evt: Event): boolean;
} & EventTarget;

export class CollectController implements ReactiveController {
  private host         : CollectHost;
  private _computeAbort: () => void;
  private _computeRun  : () => void;
  private _notifyParent: () => void;
  private _contextualChildren = new Set<HTMLElement>();
  private _prevCollect?: boolean;
  private _toggleTask  : Task<unknown[], void>;

  constructor(host: CollectHost, computeAbort: () => void, computeRun: () => void, notifyParent: () => void) {
    this.host = host;
    this._computeAbort = computeAbort;
    this._computeRun = computeRun;
    this._notifyParent = notifyParent;
    host.addController(this);

    this._toggleTask = new Task(host, {
      task: async ([noCollect]) => {
        const prev = this._prevCollect;
        this._prevCollect = !noCollect;
        if (prev === undefined) {
          if (!noCollect) this._notifyParent();
          return;
        }
        const currentCollect = !noCollect;
        if (currentCollect && !prev) {
          this._notifyParent();
        } else if (!currentCollect && prev) {
          this.host.dispatchEvent(new CollectOptionEvent({ name: this.host.storeName, options: null }));
        }
      },
      args: () => [this.host.noCollect]
    });
  }

  hostConnected() {
    this.host.addEventListener(CollectChildEvent.eventName, this._onCollectChild as EventListener);
    this.host.addEventListener(CollectChildRemovedEvent.eventName, this._onCollectChildRemoved as EventListener);
    this.host.addEventListener(CollectActionEvent.eventName, this._collectActionEvent as EventListener);
    this.host.addEventListener(CollectUpdateByDeltaEvent.eventName, this._collectUpdateByDeltaEvent as EventListener);
    this.host.addEventListener(CollectOptionEvent.eventName, this._collectOptionEvent as EventListener);

    this._announceAsContextualChild();
  }

  hostDisconnected() {
    this.host.removeEventListener(CollectChildEvent.eventName, this._onCollectChild as EventListener);
    this.host.removeEventListener(CollectChildRemovedEvent.eventName, this._onCollectChildRemoved as EventListener);
    this.host.removeEventListener(CollectActionEvent.eventName, this._collectActionEvent as EventListener);
    this.host.removeEventListener(CollectUpdateByDeltaEvent.eventName, this._collectUpdateByDeltaEvent as EventListener);
    this.host.removeEventListener(CollectOptionEvent.eventName, this._collectOptionEvent as EventListener);

    this.host._options.clear();
    this.host._actions.clear();
    this.host._partialOptions.clear();
    this.host._partialBundleOptions.clear();
    this.host._deltaUpdates.clear();
    this._contextualChildren.clear();
  }

  get toggleComplete() {
    return this._toggleTask.taskComplete;
  }

  collectOption(detail: CollectOption) {
    if (detail.options === null) {
      this.host._options.delete(detail.name);
    } else {
      this.host._options.set(detail.name, detail.options as Promise<unknown[]>);
    }

    this._computeAbort();
    this._computeRun();
    this._notifyParent();
  }

  announceRemoval() {
    this._announceContextualChildRemoved();
  }

  async waitForChildrenUpdateComplete() {
    try {
      const children = Array.from(this._contextualChildren);
      const waits = children
        .map((c) => (c as unknown as { updateComplete?: Promise<void> }).updateComplete)
        .filter((p): p is Promise<void> => !!p);
      if (waits.length > 0) await Promise.allSettled(waits);
    } catch { /* ignore */ }
  }

  getContextualChildren(): HTMLElement[] {
    return Array.from(this._contextualChildren);
  }

  private _collectOptionEvent = (e: CollectOptionEvent) => {
    if (e.target === this.host) return;
    e.stopPropagation();
    this.collectOption(e.detail);
  };

  private _collectActionEvent = (e: CollectActionEvent) => {
    if (e.target === this.host) return;
    e.stopPropagation();
    const { name, action } = e.detail;

    if (action === null) {
      this.host._actions.delete(name);
      this.host._partialOptions.delete(name);
      this.host._partialBundleOptions.delete(name);
    } else if (!action.id && (action.devices || action.subscription)) {
      this.host._actions.set(name, action);
    } else {
      if (action.bundle) {
        this.host._partialBundleOptions.set(name, action);
      } else {
        this.host._partialOptions.set(name, action);
      }
    }

    this._computeAbort();
    this._computeRun();
    this._notifyParent();
  };

  private _collectUpdateByDeltaEvent = (e: CollectUpdateByDeltaEvent) => {
    if (e.target === this.host) return;

    const { name, update } = e.detail;
    if (update === null) {
      this.host._deltaUpdates.delete(name);
    } else {
      this.host._deltaUpdates.set(name, update);
    }

    this._computeAbort();
    this._computeRun();
    this._notifyParent();
  };

  private _onCollectChild = (e: CollectChildEvent) => {
    if (e.target === this.host) return;
    e.stopPropagation();
    const { child } = e.detail;
    const hostEl = this.host as unknown as HTMLElement;
    if (child && child !== hostEl) {
      this._contextualChildren.add(child);
    }
  };

  private _onCollectChildRemoved = (e: CollectChildRemovedEvent) => {
    if (e.target === this.host) return;
    e.stopPropagation();
    const { child } = e.detail;
    const hostEl = this.host as unknown as HTMLElement;
    if (child && child !== hostEl) {
      this._contextualChildren.delete(child);
    }
  };

  private _announceAsContextualChild() {
    this.host.dispatchEvent(new CollectChildEvent({ child: this.host as unknown as HTMLElement }));
  }

  private _announceContextualChildRemoved() {
    this.host.dispatchEvent(new CollectChildRemovedEvent({ child: this.host as unknown as HTMLElement }));
  }

  hostUpdate() {
    // no-op
  }

  hostUpdated() {
    // no-op
  }
}
